// Coarse column-type mapper — turns an engine-specific column `dataType` string
// into the engine-agnostic `OperandLiteralSqlType` the operator suggester routes
// on. Used by VQE's `buildColumnOperatorOptions` so a `text` column surfaces
// `LIKE` and a numeric column surfaces `BETWEEN`, mirroring the literal-arm
// routing in `suggestOperatorsForOperand`.
//
// Conservative by design: only clearly text / numeric / boolean declared types
// map to a concrete lattice; anything ambiguous (dates, intervals, blobs, uuid,
// arrays, geometry, …) falls through to 'unknown' → the GENERIC comparison
// lattice, which is the correct, safe default for those (`=` / `<>` / ordering /
// `IN` without `LIKE`).

import type { OperandLiteralSqlType } from '../../types';

// First-token numeric type names across MSSQL / PostgreSQL / MySQL / Oracle /
// SQLite. Matched against the leading word of the (precision-stripped) type so
// `INT UNSIGNED`, `DOUBLE PRECISION`, `NUMERIC(10,2)` all resolve correctly.
const NUMERIC_FIRST_TOKENS: ReadonlySet<string> = new Set([
  'INT', 'INTEGER', 'BIGINT', 'SMALLINT', 'TINYINT', 'MEDIUMINT',
  'INT2', 'INT4', 'INT8',
  'DECIMAL', 'NUMERIC', 'NUMBER', 'DEC', 'FIXED',
  'FLOAT', 'FLOAT4', 'FLOAT8', 'REAL', 'DOUBLE',
  'MONEY', 'SMALLMONEY',
  'SERIAL', 'BIGSERIAL', 'SMALLSERIAL', 'SERIAL2', 'SERIAL4', 'SERIAL8',
  'BINARY_FLOAT', 'BINARY_DOUBLE',
]);

const BOOLEAN_FIRST_TOKENS: ReadonlySet<string> = new Set(['BOOL', 'BOOLEAN']);

export function columnSqlTypeFromDataType(
  dataType: string | undefined,
): OperandLiteralSqlType {
  if (!dataType) return 'unknown';
  // Strip precision/length (`VARCHAR(255)` → `VARCHAR`) and take the leading
  // word (`DOUBLE PRECISION` → `DOUBLE`, `CHARACTER VARYING` → `CHARACTER`).
  const base = dataType.trim().toUpperCase().replace(/\(.*$/s, '').trim();
  const firstToken = base.split(/\s+/)[0] ?? '';

  // Text family: any type carrying CHAR / TEXT / CLOB is text, plus bare STRING.
  // No numeric/temporal type contains those substrings, so this is false-
  // positive free.
  if (
    firstToken.includes('CHAR') ||
    firstToken.includes('TEXT') ||
    firstToken.includes('CLOB') ||
    firstToken === 'STRING'
  ) {
    return 'text';
  }
  if (BOOLEAN_FIRST_TOKENS.has(firstToken)) return 'boolean';
  if (NUMERIC_FIRST_TOKENS.has(firstToken)) return 'numeric';
  return 'unknown';
}
